import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SEO } from '../components/SEO';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form is UI only per requirements
    console.log('Form submitted:', formData);
    alert('Thank you for your message. We will get back to you shortly.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      interest: '',
      message: ''
    });
  };
  return <main data-id="element-320">
      <SEO title="Contact Us | Free Consultation" description="Contact Ambient Solutions LLC in Columbia, MD for a free consultation. Learn how to hire your family member as a paid professional caregiver." keywords="contact home health agency, Columbia MD home care contact, free caregiver consultation" data-id="element-321" />
      <PageHero title="Contact Us" subtitle="Get started today — your family caregiver can be certified in 24-48 hours" data-id="element-322" />

      <section className="py-24 px-4 bg-white" data-id="element-323">
        <div className="max-w-7xl mx-auto" data-id="element-324">
          <div className="flex flex-col lg:flex-row gap-16" data-id="element-325">
            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex-[3]" data-id="element-326">
              <h2 className="text-3xl font-heading font-bold text-neutral-dark mb-8" data-id="element-327">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-328">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-329">
                  <div data-id="element-330">
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-dark mb-2" data-id="element-331">
                      Full Name
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="John Doe" data-id="element-332" />
                  </div>
                  <div data-id="element-333">
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-dark mb-2" data-id="element-334">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="john@example.com" data-id="element-335" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-336">
                  <div data-id="element-337">
                    <label htmlFor="phone" className="block text-sm font-medium text-neutral-dark mb-2" data-id="element-338">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" placeholder="(866) 652-8987" data-id="element-339" />
                  </div>
                  <div data-id="element-340">
                    <label htmlFor="interest" className="block text-sm font-medium text-neutral-dark mb-2" data-id="element-341">
                      I am interested in...
                    </label>
                    <select id="interest" name="interest" value={formData.interest} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white" data-id="element-342">
                      <option value="" disabled data-id="element-343">
                        Select an option
                      </option>
                      <option value="Skilled Nursing" data-id="element-344">Skilled Nursing</option>
                      <option value="Personal Care" data-id="element-345">Personal Care</option>
                      <option value="Companion Care" data-id="element-346">Companion Care</option>
                      <option value="Alzheimer's/Dementia Care" data-id="element-347">
                        Alzheimer's/Dementia Care
                      </option>
                      <option value="Respite Care" data-id="element-348">Respite Care</option>
                      <option value="Other" data-id="element-349">Other</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-350">
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-dark mb-2" data-id="element-351">
                    Message
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none" placeholder="How can we help you?" data-id="element-352"></textarea>
                </div>

                <button type="submit" className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-md hover:shadow-lg w-full md:w-auto" data-id="element-353">
                  Submit Request
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex-[2] space-y-6" data-id="element-354">
              <h2 className="text-3xl font-heading font-bold text-neutral-dark mb-8" data-id="element-355">
                Contact Information
              </h2>

              <div className="bg-warm p-8 rounded-2xl border border-gray-100 space-y-8" data-id="element-356">
                <div className="flex items-start gap-4" data-id="element-357">
                  <div className="bg-white p-3 rounded-full shadow-sm text-primary" data-id="element-358">
                    <PhoneIcon className="w-6 h-6" data-id="element-359" />
                  </div>
                  <div data-id="element-360">
                    <h3 className="font-bold text-neutral-dark mb-1" data-id="element-361">Phone</h3>
                    <a href="tel:+18666528987" className="text-neutral-medium hover:text-primary transition-colors text-lg" data-id="element-362">
                      (866) 652-8987
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-363">
                  <div className="bg-white p-3 rounded-full shadow-sm text-primary" data-id="element-364">
                    <MailIcon className="w-6 h-6" data-id="element-365" />
                  </div>
                  <div data-id="element-366">
                    <h3 className="font-bold text-neutral-dark mb-1" data-id="element-367">Email</h3>
                    <a href="mailto:info@ambienthomecare.com" className="text-neutral-medium hover:text-primary transition-colors text-lg" data-id="element-368">
                      info@ambienthomecare.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-369">
                  <div className="bg-white p-3 rounded-full shadow-sm text-primary" data-id="element-370">
                    <MapPinIcon className="w-6 h-6" data-id="element-371" />
                  </div>
                  <div data-id="element-372">
                    <h3 className="font-bold text-neutral-dark mb-1" data-id="element-373">
                      Address
                    </h3>
                    <p className="text-neutral-medium text-lg" data-id="element-374">
                      10632 Little Patuxent Parkway
                      <br data-id="element-375" />
                      Columbia, MD 21044
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-376">
                  <div className="bg-white p-3 rounded-full shadow-sm text-primary" data-id="element-377">
                    <ClockIcon className="w-6 h-6" data-id="element-378" />
                  </div>
                  <div data-id="element-379">
                    <h3 className="font-bold text-neutral-dark mb-1" data-id="element-380">Hours</h3>
                    <p className="text-neutral-medium text-lg" data-id="element-381">
                      Mon - Fri: 8:00 AM - 6:00 PM
                      <br data-id="element-382" />
                      Sat - Sun: Closed
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Map Placeholder */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mt-16 rounded-3xl overflow-hidden shadow-lg h-[400px]" data-id="element-383">
            <iframe title="Ambient Solutions LLC Location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3091.234!2d-76.8616!3d39.2137!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7dfb1a3c3e3e3%3A0x0!2s10632+Little+Patuxent+Parkway%2C+Columbia%2C+MD+21044!5e0!3m2!1sen!2sus!4v1" width="100%" height="100%" style={{
            border: 0
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-384" />
          </motion.div>
        </div>
      </section>
    </main>;
}